import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import './InvestorManagementModal.css';

const InvestorManagementModal = ({ isOpen, onClose, pool, onInvestorsUpdated }) => {
  const { showToast } = useToast();
  const [investors, setInvestors] = useState([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('investors');
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [processingWithdrawal, setProcessingWithdrawal] = useState(null);

  useEffect(() => {
    if (isOpen && pool) {
      fetchInvestors();
      fetchWithdrawalRequests();
    }
  }, [isOpen, pool]);

  const fetchInvestors = async () => {
    try {
      const response = await fetch(`/api/admin/pools/${pool.id}/investors`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setInvestors(data.investors || []);
      }
    } catch (error) {
      console.error('Error fetching investors:', error);
      showToast('Failed to load investors', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchWithdrawalRequests = async () => {
    try {
      const response = await fetch(`/api/admin/pools/${pool.id}/withdrawal-requests`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setWithdrawalRequests(data.requests || []);
      }
    } catch (error) {
      console.error('Error fetching withdrawal requests:', error);
    }
  };

  const processWithdrawalRequest = async (requestId, action) => {
    setProcessingWithdrawal(requestId);
    try {
      const response = await fetch(`/api/admin/pools/withdrawal-requests/${requestId}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        showToast(`Withdrawal request ${action}d successfully`, 'success');
        fetchWithdrawalRequests();
        fetchInvestors();
        onInvestorsUpdated();
      } else {
        const error = await response.json();
        throw new Error(error.message || `Failed to ${action} withdrawal request`);
      }
    } catch (error) {
      console.error(`Error ${action}ing withdrawal:`, error);
      showToast(error.message, 'error');
    } finally {
      setProcessingWithdrawal(null);
    }
  };

  const removeInvestor = async (investorId) => {
    if (!window.confirm('Are you sure you want to remove this investor? This will process their full withdrawal.')) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/pools/${pool.id}/investors/${investorId}/remove`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        showToast('Investor removed successfully', 'success');
        fetchInvestors();
        onInvestorsUpdated();
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Failed to remove investor');
      }
    } catch (error) {
      console.error('Error removing investor:', error);
      showToast(error.message, 'error');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    }).format(amount);
  };

  const getPerformanceColor = (performance) => {
    if (performance > 0) return 'positive';
    if (performance < 0) return 'negative';
    return 'neutral';
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="investor-management-modal">
        <div className="modal-header">
          <h2>Investor Management - {pool.name}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab-btn ${activeTab === 'investors' ? 'active' : ''}`}
            onClick={() => setActiveTab('investors')}
          >
            Investors ({investors.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'withdrawals' ? 'active' : ''}`}
            onClick={() => setActiveTab('withdrawals')}
          >
            Withdrawal Requests ({withdrawalRequests.length})
          </button>
        </div>

        <div className="modal-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading investor data...</p>
            </div>
          ) : (
            <>
              {activeTab === 'investors' && (
                <div className="investors-tab">
                  <div className="investors-summary">
                    <div className="summary-stats">
                      <div className="stat">
                        <span className="stat-label">Total Investors</span>
                        <span className="stat-value">{investors.length}</span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Total Invested</span>
                        <span className="stat-value">
                          {formatCurrency(investors.reduce((sum, inv) => sum + inv.amountInvested, 0))} SOL
                        </span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Current Value</span>
                        <span className="stat-value">
                          {formatCurrency(investors.reduce((sum, inv) => sum + inv.currentValue, 0))} SOL
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="investors-list">
                    {investors.length === 0 ? (
                      <div className="no-investors">
                        <p>No investors in this pool yet.</p>
                      </div>
                    ) : (
                      <div className="investors-table">
                        <div className="table-header">
                          <div className="header-cell">Investor</div>
                          <div className="header-cell">Invested</div>
                          <div className="header-cell">Current Value</div>
                          <div className="header-cell">Return</div>
                          <div className="header-cell">Shares</div>
                          <div className="header-cell">Join Date</div>
                          <div className="header-cell">Actions</div>
                        </div>
                        
                        {investors.map((investor, index) => (
                          <div key={index} className="table-row">
                            <div className="cell investor-info">
                              <div className="investor-details">
                                <span className="investor-email">{investor.email}</span>
                                <span className="investor-id">ID: {investor.userId}</span>
                              </div>
                            </div>
                            <div className="cell">
                              {formatCurrency(investor.amountInvested)} SOL
                            </div>
                            <div className="cell">
                              {formatCurrency(investor.currentValue)} SOL
                            </div>
                            <div className={`cell ${getPerformanceColor(investor.return)}`}>
                              {investor.return > 0 ? '+' : ''}{investor.return.toFixed(2)}%
                            </div>
                            <div className="cell">
                              {formatCurrency(investor.shares)}
                            </div>
                            <div className="cell">
                              {new Date(investor.investmentDate).toLocaleDateString()}
                            </div>
                            <div className="cell actions">
                              <button
                                className="action-btn view-btn"
                                onClick={() => setSelectedInvestor(investor)}
                              >
                                View
                              </button>
                              <button
                                className="action-btn remove-btn"
                                onClick={() => removeInvestor(investor.id)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'withdrawals' && (
                <div className="withdrawals-tab">
                  <div className="withdrawals-summary">
                    <div className="summary-stats">
                      <div className="stat">
                        <span className="stat-label">Pending Requests</span>
                        <span className="stat-value">
                          {withdrawalRequests.filter(req => req.status === 'pending').length}
                        </span>
                      </div>
                      <div className="stat">
                        <span className="stat-label">Total Amount</span>
                        <span className="stat-value">
                          {formatCurrency(withdrawalRequests.reduce((sum, req) => sum + req.estimatedAmount, 0))} SOL
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="withdrawal-requests-list">
                    {withdrawalRequests.length === 0 ? (
                      <div className="no-requests">
                        <p>No withdrawal requests.</p>
                      </div>
                    ) : (
                      <div className="requests-table">
                        <div className="table-header">
                          <div className="header-cell">Investor</div>
                          <div className="header-cell">Shares</div>
                          <div className="header-cell">Estimated Amount</div>
                          <div className="header-cell">Status</div>
                          <div className="header-cell">Requested</div>
                          <div className="header-cell">Actions</div>
                        </div>
                        
                        {withdrawalRequests.map((request, index) => (
                          <div key={index} className="table-row">
                            <div className="cell">
                              <div className="investor-details">
                                <span className="investor-email">{request.investorEmail}</span>
                                <span className="investor-id">ID: {request.userId}</span>
                              </div>
                            </div>
                            <div className="cell">
                              {formatCurrency(request.sharesToWithdraw)}
                            </div>
                            <div className="cell">
                              {formatCurrency(request.estimatedAmount)} SOL
                            </div>
                            <div className={`cell status-${request.status}`}>
                              {request.status.toUpperCase()}
                            </div>
                            <div className="cell">
                              {new Date(request.requestedAt).toLocaleDateString()}
                            </div>
                            <div className="cell actions">
                              {request.status === 'pending' && (
                                <>
                                  <button
                                    className="action-btn approve-btn"
                                    onClick={() => processWithdrawalRequest(request.id, 'approve')}
                                    disabled={processingWithdrawal === request.id}
                                  >
                                    {processingWithdrawal === request.id ? 'Processing...' : 'Approve'}
                                  </button>
                                  <button
                                    className="action-btn reject-btn"
                                    onClick={() => processWithdrawalRequest(request.id, 'reject')}
                                    disabled={processingWithdrawal === request.id}
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                              {request.status === 'processing' && (
                                <button
                                  className="action-btn complete-btn"
                                  onClick={() => processWithdrawalRequest(request.id, 'complete')}
                                  disabled={processingWithdrawal === request.id}
                                >
                                  {processingWithdrawal === request.id ? 'Processing...' : 'Complete'}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Investor Detail Modal */}
        {selectedInvestor && (
          <div className="investor-detail-overlay">
            <div className="investor-detail-modal">
              <div className="detail-header">
                <h3>Investor Details</h3>
                <button 
                  className="close-btn"
                  onClick={() => setSelectedInvestor(null)}
                >
                  ×
                </button>
              </div>
              
              <div className="detail-content">
                <div className="detail-section">
                  <h4>Basic Information</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="label">Email:</span>
                      <span className="value">{selectedInvestor.email}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">User ID:</span>
                      <span className="value">{selectedInvestor.userId}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Investment Date:</span>
                      <span className="value">{new Date(selectedInvestor.investmentDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h4>Investment Details</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="label">Amount Invested:</span>
                      <span className="value">{formatCurrency(selectedInvestor.amountInvested)} SOL</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Current Value:</span>
                      <span className="value">{formatCurrency(selectedInvestor.currentValue)} SOL</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Shares Owned:</span>
                      <span className="value">{formatCurrency(selectedInvestor.shares)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Return:</span>
                      <span className={`value ${getPerformanceColor(selectedInvestor.return)}`}>
                        {selectedInvestor.return > 0 ? '+' : ''}{selectedInvestor.return.toFixed(2)}%
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Fees Paid:</span>
                      <span className="value">{formatCurrency(selectedInvestor.feesPaid || 0)} SOL</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvestorManagementModal;